export type ConfirmLevel = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  level?: ConfirmLevel;
  /** When true, only the confirm button renders. Useful for info-only dialogs
   *  where the user just needs to acknowledge a message. Backdrop / Escape still
   *  dismisses (resolves false). */
  hideCancel?: boolean;
}

interface LevelConfig {
  headerClass: string;
  titleClass: string;
  iconSvg: string;
  confirmBtnClass: string;
  defaultTitle: string;
}

const LEVELS: Record<ConfirmLevel, LevelConfig> = {
  danger: {
    headerClass: 'bg-danger bg-opacity-10 border-bottom border-danger-subtle',
    titleClass: 'text-danger-emphasis',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16" class="text-danger-emphasis flex-shrink-0" aria-hidden="true"><path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5z"/></svg>`,
    confirmBtnClass: 'btn-danger',
    defaultTitle: 'Confirm deletion',
  },
  warning: {
    headerClass: 'bg-warning bg-opacity-10 border-bottom border-warning-subtle',
    titleClass: 'text-warning-emphasis',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16" class="text-warning-emphasis flex-shrink-0" aria-hidden="true"><path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/></svg>`,
    confirmBtnClass: 'btn-warning',
    defaultTitle: 'Please confirm',
  },
  info: {
    headerClass: 'bg-primary bg-opacity-10 border-bottom border-primary-subtle',
    titleClass: 'text-primary-emphasis',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16" class="text-primary-emphasis flex-shrink-0" aria-hidden="true"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/><path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/></svg>`,
    confirmBtnClass: 'btn-primary',
    defaultTitle: 'Please confirm',
  },
  success: {
    headerClass: 'bg-success bg-opacity-10 border-bottom border-success-subtle',
    titleClass: 'text-success-emphasis',
    iconSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" viewBox="0 0 16 16" class="text-success-emphasis flex-shrink-0" aria-hidden="true"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/></svg>`,
    confirmBtnClass: 'btn-success',
    defaultTitle: 'Please confirm',
  },
};

// LIFO stack so Escape closes the topmost confirm without affecting earlier ones.
const confirmStack: Array<() => void> = [];
if (typeof document !== 'undefined' && !(document as unknown as { __acteazeConfirmEscWired?: boolean }).__acteazeConfirmEscWired) {
  (document as unknown as { __acteazeConfirmEscWired?: boolean }).__acteazeConfirmEscWired = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || confirmStack.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    confirmStack[confirmStack.length - 1]?.();
  }, true);
}

/**
 * Drop-in replacement for window.confirm() with consistent styling.
 * Usage:
 *   if (!await confirmDialog({ message: 'Delete this row?', level: 'danger' })) return;
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const level = LEVELS[options.level ?? 'danger'];
    const title = options.title ?? level.defaultTitle;
    const confirmLabel = options.confirmLabel ?? (options.level === 'danger' || options.level == null ? 'Delete' : 'Confirm');
    const cancelLabel = options.cancelLabel ?? 'Cancel';

    // Remember the previously-focused element so we can restore it afterwards.
    const returnFocusTo: HTMLElement | null = (document.activeElement instanceof HTMLElement)
      ? document.activeElement
      : null;

    // Backdrop (semi-opaque, z-index above drawers which sit at 1070+ offcanvas-level).
    const backdrop = document.createElement('div');
    backdrop.className = 'position-fixed top-0 start-0 w-100 h-100';
    backdrop.style.backgroundColor = 'rgba(0,0,0,0.5)';
    backdrop.style.zIndex = '1090';

    // Dialog container (centred).
    const host = document.createElement('div');
    host.className = 'position-fixed top-50 start-50 translate-middle';
    host.style.zIndex = '1091';
    host.style.maxWidth = 'min(480px, calc(100vw - 2rem))';
    host.style.width = '100%';
    host.setAttribute('role', 'alertdialog');
    host.setAttribute('aria-modal', 'true');

    host.innerHTML = `
      <div class="card shadow-lg border-0">
        <div class="card-header ${level.headerClass} d-flex align-items-center gap-3 py-3 px-3">
          ${level.iconSvg}
          <h5 class="mb-0 ${level.titleClass} flex-grow-1">${esc(title)}</h5>
        </div>
        <div class="card-body px-3 py-3">
          <p class="mb-0">${esc(options.message)}</p>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2 bg-body-tertiary py-2 px-3">
          ${options.hideCancel ? '' : `<button type="button" class="btn btn-outline-secondary btn-sm" data-action="cancel">${esc(cancelLabel)}</button>`}
          <button type="button" class="btn ${level.confirmBtnClass} btn-sm" data-action="confirm">${esc(confirmLabel)}</button>
        </div>
      </div>
    `;

    let settled = false;
    const close = (result: boolean): void => {
      if (settled) return;
      settled = true;
      const idx = confirmStack.lastIndexOf(closeAsCancel);
      if (idx >= 0) confirmStack.splice(idx, 1);
      host.remove();
      backdrop.remove();
      if (returnFocusTo && document.contains(returnFocusTo)) {
        try { returnFocusTo.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
      resolve(result);
    };
    const closeAsCancel = (): void => close(false);
    confirmStack.push(closeAsCancel);

    backdrop.addEventListener('click', closeAsCancel);
    host.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', closeAsCancel);
    const confirmBtn = host.querySelector<HTMLButtonElement>('[data-action="confirm"]')!;
    confirmBtn.addEventListener('click', () => close(true));

    // Enter confirms; block clicks from bubbling to the backdrop.
    host.addEventListener('click', (e) => e.stopPropagation());
    host.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.defaultPrevented) {
        e.preventDefault();
        close(true);
      }
    });

    document.body.appendChild(backdrop);
    document.body.appendChild(host);

    // Focus the primary action after the DOM settles (so Enter works immediately).
    window.setTimeout(() => confirmBtn.focus(), 0);
  });
}

function esc(v: string): string {
  return (v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
